import { useState, useEffect } from 'react'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import { useAuth } from '../context/AuthContext'
import { useDepartments } from '../shared/hooks/useDepartments'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jira-clone/ui'
import { Loader2, Save, Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import {
  Switch,
  PermissionToggleGrid,
  togglePermissionEntry,
  toggleAllModuleActions,
} from '../shared/components/PermissionToggleGrid'

interface UserManageModalProps {
  userId: string
  userName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}

export function UserManageModal({
  userId,
  userName,
  open,
  onOpenChange,
  onSaved,
}: UserManageModalProps) {
  const { user: currentUser, refreshAuth } = useAuth()
  const { data: departments = [] } = useDepartments()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [newPassword, setNewPassword] = useState('')

  const [form, setForm] = useState({
    name: '',
    email: '',
    employeeId: '',
    department: '',
    departmentRole: '',
    status: 'ACTIVE',
  })

  const [permissions, setPermissions] = useState<Array<{ module: string; actions: string[] }>>([])

  const isSuperadmin = isSuperAdminOrOwner(currentUser?.role)

  useEffect(() => {
    if (!open || !userId) return
    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const userRes = await api.get<any>(`/users/${userId}`)
        if (cancelled) return

        const ud = userRes.data || userRes
        setForm({
          name: ud.name || '',
          email: ud.email || '',
          employeeId: ud.employeeId || '',
          department: ud.department || '',
          departmentRole: ud.departmentRole || '',
          status: ud.status || 'ACTIVE',
        })
        setPermissions(ud.permissions || [])
      } catch (err) {
        console.error('Failed to load user data:', err)
        toast.error('Failed to load user data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [open, userId])

  const togglePermission = (module: string, action: string) => {
    setPermissions((prev) => togglePermissionEntry(prev, module, action))
  }

  const toggleAll = (module: string, enable: boolean) => {
    setPermissions((prev) => toggleAllModuleActions(prev, module, enable))
  }

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    setResetting(true)
    try {
      await api.post(`/users/${userId}/reset-password`, { password: newPassword }, { skipErrorToast: true })
      toast.success('Password reset successfully')
      setShowReset(false)
      setNewPassword('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'reset this password'))
    } finally {
      setResetting(false)
    }
  }

  const saveChanges = async () => {
    setSaving(true)
    try {
      await Promise.all([
        api.patch(
          `/users/${userId}`,
          { ...form, employeeId: form.employeeId.trim() || undefined },
          { skipErrorToast: true }
        ),
        api.patch(`/users/${userId}/permissions`, { permissions }, { skipErrorToast: true }),
      ])
      toast.success('User updated successfully')

      // If updating self, refresh current auth permissions
      if (currentUser?.id === userId) {
        await refreshAuth()
      }

      onSaved?.()
      onOpenChange(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save changes to this user'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#6E11E3]" />
            Manage Access — {userName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="max-h-[calc(90vh-200px)] overflow-y-auto pr-2 space-y-4">
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info">User Info</TabsTrigger>
                <TabsTrigger value="permissions">Module Access</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      disabled={!isSuperadmin}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      disabled={!isSuperadmin}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="employeeId">Employee ID</Label>
                    <Input
                      id="employeeId"
                      placeholder="e.g. EMP-001"
                      value={form.employeeId}
                      onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                      disabled={!isSuperadmin}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="department">Department</Label>
                    <select
                      id="department"
                      value={form.department}
                      onChange={(e) => setForm({ ...form, department: e.target.value })}
                      disabled={!isSuperadmin}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                    >
                      <option value="">Select a department</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.name}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="departmentRole">Role / Title</Label>
                    <Input
                      id="departmentRole"
                      value={form.departmentRole}
                      onChange={(e) => setForm({ ...form, departmentRole: e.target.value })}
                      disabled={!isSuperadmin}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="status">Account Active</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch
                        checked={form.status === 'ACTIVE'}
                        onCheckedChange={(checked) =>
                          setForm({ ...form, status: checked ? 'ACTIVE' : 'INACTIVE' })
                        }
                        disabled={!isSuperadmin}
                      />
                      <span className="text-xs text-muted-foreground">
                        {form.status === 'ACTIVE'
                          ? 'Active — can log in'
                          : 'Inactive — login blocked'}
                      </span>
                    </div>
                  </div>
                </div>

                {isSuperadmin && (
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold">Reset Password</h4>
                        <p className="text-xs text-muted-foreground">
                          Set a temporary new password for this user
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowReset(!showReset)}
                      >
                        {showReset ? 'Cancel' : 'Reset Password'}
                      </Button>
                    </div>
                    {showReset && (
                      <div className="mt-3 flex gap-2">
                        <Input
                          type="password"
                          placeholder="New password (min 6 characters)"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <Button onClick={resetPassword} disabled={resetting}>
                          {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="permissions" className="mt-4">
                <p className="text-xs text-muted-foreground mb-3">
                  Toggle module permissions for this user (Actions: VIEW, CREATE, EDIT, DELETE, ASSIGN):
                </p>
                <PermissionToggleGrid
                  permissions={permissions}
                  onToggle={togglePermission}
                  onToggleAll={toggleAll}
                  disabled={!isSuperadmin}
                />
              </TabsContent>

            </Tabs>
          </div>
        )}

        <DialogFooter className="border-t border-border pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={saveChanges}
            disabled={saving || loading || !isSuperadmin}
            className="bg-[#6E11E3] hover:bg-[#5B13B8] text-white"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
